import React from 'react';
import { T } from '../constants';
import { fmtDate, fmtTime, mlColor, dirStyle, sizeCategory } from '../helpers';
import GradeBadge from './badges/GradeBadge';
import SystemBadge from './badges/SystemBadge';
import SlotLeaders from './SlotLeaders';

function accentFor(action) {
  if (action === 'TRADE')   return { accent: T.bull,      bg: T.bullBg,    border: T.bullBorder };
  if (action === 'OBSERVE') return { accent: T.purple,    bg: T.purpleBg,  border: T.purpleBorder };
  if (action === 'TRAIL')   return { accent: '#f59e0b',   bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.3)' };
  return                           { accent: T.bear,      bg: T.bearBg,    border: T.bearBorder };
}

function ToastCard({ id, decision: d, dur, onClose, onPin }) {
  // TIER strategies put the verdict in `action_label`, not `action`. Prefer it.
  const actionEff = d.action_label || d.action;
  const { accent, bg, border } = accentFor(actionEff);
  const isTrade  = actionEff === 'TRADE';
  const isObs    = actionEff === 'OBSERVE';
  const isTrail  = actionEff === 'TRAIL';
  const isStop   = actionEff === 'STOP LOSS';
  const sc = sizeCategory(d);   // Concentrate size weight — shown on a TRADE so the desk sizes before entry

  return (
    <div className="toast-card" style={{
      background: '#ffffff',
      border:     `1px solid ${border}`,
      borderLeft: `3px solid ${accent}`,
      boxShadow:  `0 10px 30px rgba(20,30,55,0.12), 0 0 24px ${accent}22`,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', background: bg, borderBottom: `1px solid ${border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: accent,
            animation: isTrade ? 'pulse 1.5s infinite' : 'none',
          }}>
            ● {isTrade ? 'New Trade' : isObs ? 'Observe Signal' : isStop ? '🛑 Stop Loss' : isTrail ? (d._trail_armed ? '🔒 Trail Armed' : '📈 Trail Update') : 'New Decision'}
          </span>
          <SystemBadge systemId={d.system_id} />
          {d._trigger && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: T.text,
              padding: '1px 6px', borderRadius: 3,
              border: `1px solid ${border}`, background: 'rgba(255,255,255,0.6)',
              fontFamily: 'Roboto Mono, monospace', letterSpacing: '0.04em',
            }}
            title={d.signal_id || ''}>
              {d._trigger}
              {d._isLuxConfluencing && (
                <span style={{ color: T.textMuted, marginLeft: 4, fontSize: 9 }}>+ LUX</span>
              )}
            </span>
          )}
        </div>
        <button onClick={() => onClose(id)} style={{
          background: 'none', border: 'none', color: T.textMuted,
          fontSize: 16, lineHeight: 1, padding: '2px 4px', cursor: 'pointer',
        }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px' }}>
        {/* ── TRAIL UPDATE body ── */}
        {isTrail && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span className="mono" style={{ fontSize: 16, fontWeight: 800, color: accent, lineHeight: 1 }}>
                {d._trail_label || 'TRAIL UPDATE'}
              </span>
              {d.direction && (
                <span className="badge" style={{ fontSize: 9, padding: '1px 5px',
                  background: d.direction === 'LONG' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                  color: d.direction === 'LONG' ? '#10b981' : '#ef4444',
                  border: `1px solid ${d.direction === 'LONG' ? '#10b98144' : '#ef444444'}`,
                  borderRadius: 3, fontWeight: 700 }}>
                  {d.direction}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, fontFamily: 'Roboto Mono, monospace', marginBottom: 6 }}>
              <span style={{ color: '#94a3b8' }}>SL now </span>
              <span style={{ color: '#ef4444', fontWeight: 800, fontSize: 15 }}>{d._trail_sl}</span>
              {d._trail_peak && (
                <span style={{ color: '#f59e0b', marginLeft: 8, fontSize: 11 }}>peak {d._trail_peak}</span>
              )}
            </div>
            <div style={{ paddingTop: 8, borderTop: `1px solid ${border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="mono" style={{ fontSize: 9, color: '#94a3b8' }}>
                {fmtDate(d.signal_ts)} · {fmtTime(d.signal_ts)}
              </span>
              <span className="mono" style={{ fontSize: 9, color: '#94a3b8' }}>TIER 1 · atr_trail</span>
            </div>
          </div>
        )}
        {/* ── Normal decision body ── */}
        {!isTrail && <>
        {d._slotLeaders && d._slotLeaders.length > 0 && (
          <div style={{ marginBottom: 8 }}><SlotLeaders leaders={d._slotLeaders} /></div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="mono" style={{
            fontSize: 22, fontWeight: 800, color: accent,
            textShadow: `0 0 16px ${accent}60`, lineHeight: 1,
          }}>{actionEff}</span>
          {d.direction && (
            <span className="badge" style={{ ...dirStyle(d.direction), fontSize: 9 }}>{d.direction}</span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <GradeBadge grade={d.grade} decision={d} />
          <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: mlColor(d.ml_score) }}>
            {Math.round(parseFloat(d.ml_score) || 0)}
          </span>
          {isTrade && d.contracts != null && (
            <span className="mono" style={{ fontSize: 11, color: T.bull }}>{d.contracts}c</span>
          )}
          {/* CAT 1/2/3 sizing badge — shown on every toast (not just TRADE) so the
              desk sees the concentrate weight on OBSERVE / EXIT / STOP signals too.
              Renders only when sizeCategory(d) returns a value (i.e., the strategy
              is in the Concentrate book; IMPERIAL etc. stay badge-less). */}
          {sc && (
            <span className="mono" title={sc.tip} style={{
              fontSize: 11, fontWeight: 700, color: sc.color,
              padding: '1px 6px', borderRadius: 3,
              background: `${sc.color}14`, border: `1px solid ${sc.color}40`,
            }}>
              {sc.catLabel}
            </span>
          )}
          {isTrade && d.entry_price != null && d.cat_stop_bp != null && (() => {
            const offset = d.entry_price * d.cat_stop_bp / 10000;
            const side   = (d.side || d.direction || '').toLowerCase();
            const stopPx = side === 'short'
              ? d.entry_price + offset
              : d.entry_price - offset;
            return (
              <span className="mono" style={{
                fontSize: 11, fontWeight: 700, color: T.bear,
                padding: '1px 6px', borderRadius: 3,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.3)',
              }}>
                Stop {stopPx.toFixed(2)}
              </span>
            );
          })()}
          {!isTrade && d.gate_blocked && (
            <span className="mono" style={{ fontSize: 10, color: T.textMuted }}>
              {d.gate_blocked.slice(0, 16)}
            </span>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 8, borderTop: `1px solid ${border}`,
        }}>
          <span className="mono" style={{ fontSize: 9, color: T.textMuted }}>
            {fmtDate(d.signal_ts)} · {fmtTime(d.signal_ts)}
          </span>
          <button onClick={() => onPin(d)} style={{
            padding: '3px 10px', background: 'transparent',
            border: `1px solid ${border}`, color: accent,
            borderRadius: 3, fontSize: 10, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
          }}>View</button>
        </div>
        </>}
      </div>

      {/* Per-card progress bar */}
      <div className="toast-bar-wrap">
        <div className="toast-bar" style={{ background: accent, '--toast-dur': `${dur}ms` }} />
      </div>
    </div>
  );
}

export default function Toast({ toasts, onClose, onCloseAll, onPin }) {
  if (!toasts.length) return null;

  return (
    <div className="toast-stack">
      {toasts.length > 1 && (
        <button className="toast-dismiss-all" onClick={onCloseAll}>
          Dismiss all ({toasts.length})
        </button>
      )}
      {toasts.map(({ id, decision, dur }) => (
        <ToastCard
          key={id}
          id={id}
          decision={decision}
          dur={dur}
          onClose={onClose}
          onPin={onPin}
        />
      ))}
    </div>
  );
}
